/**
 * ZENTRALER SCAN RESOLVER
 * 
 * Einheitliche Logik für QR-Code-Scans in der gesamten VIVI App.
 * 
 * Features:
 * - Konsistente Identifikation über API
 * - Unterstützt Standorte, Automaten, Produkte
 * - Einheitliche Fehlerbehandlung
 * - Kontextabhängige Weiterleitung
 * 
 * @version 1.6
 */

/**
 * Scan-Ergebnis Typen
 */
export const ScanResultType = {
  STANDORT: 'standort',
  AUTOMAT: 'automat',
  PRODUKT: 'produkt',
  UNKNOWN: 'unknown',
  ERROR: 'error'
};

/**
 * Scan-Kontext - bestimmt was nach dem Scan passiert
 */
export const ScanContext = {
  // Allgemein - intelligente Weiterleitung
  UNIVERSAL: 'universal',
  
  // Spezifische Kontexte
  AUTOMAT_SUCHE: 'automat_suche',
  STANDORT_ZUWEISUNG: 'standort_zuweisung',
  BEFUELLUNG: 'befuellung',
  AUSZAEHLEN: 'auszaehlen',
  INVENTAR: 'inventar'
};

/**
 * Zentrale Scan-Resolver Funktion
 * 
 * @param {Object} params - Parameter
 * @param {string} params.qrValue - Der gescannte QR-Code Wert
 * @param {Object} params.api - Axios Instance oder API-URL
 * @param {Object} [params.user] - Aktueller User (für Berechtigungsprüfung)
 * @param {string} [params.context] - Scan-Kontext (aus ScanContext)
 * 
 * @returns {Promise<Object>} - Scan-Ergebnis
 */
export const resolveScan = async ({ qrValue, api, user = null, context = ScanContext.UNIVERSAL }) => {
  // Normalisiere den Code
  const normalizedCode = (qrValue || '').trim();
  
  if (!normalizedCode) {
    return {
      type: ScanResultType.ERROR,
      message: 'Leerer QR-Code',
      code: normalizedCode
    };
  }

  console.log(`[ScanResolver] Suche: '${normalizedCode}' (Kontext: ${context})`);

  // API-URL bestimmen
  const apiUrl = typeof api === 'string' ? api : api;

  // Ergebnis-Objekt
  const result = {
    type: ScanResultType.UNKNOWN,
    code: normalizedCode,
    data: null,
    message: '',
    navigateTo: null
  };

  try {
    // 1. STANDORT SUCHEN
    try {
      console.log(`[ScanResolver] Suche Standort...`);
      const standortRes = await fetch(`${apiUrl}/standorte/qr/${encodeURIComponent(normalizedCode)}`, {
        headers: getAuthHeaders()
      });
      
      if (standortRes.ok) {
        const standort = await standortRes.json();
        console.log(`[ScanResolver] Standort gefunden: ${standort.name}`);
        
        result.type = ScanResultType.STANDORT;
        result.data = standort;
        result.message = `Standort gefunden: ${standort.name}`;
        
        // Navigations-Ziel basierend auf Kontext
        if (context === ScanContext.UNIVERSAL || context === ScanContext.STANDORT_ZUWEISUNG) {
          result.navigateTo = `/standorte/scan/${encodeURIComponent(normalizedCode)}`;
        } else {
          result.navigateTo = `/standorte/${standort.id}`;
        }
        
        return result;
      }
    } catch (e) {
      console.log(`[ScanResolver] Kein Standort gefunden`);
    }

    // 2. AUTOMAT SUCHEN
    try {
      console.log(`[ScanResolver] Suche Automat...`);
      const automatRes = await fetch(`${apiUrl}/automaten/qr/${encodeURIComponent(normalizedCode)}`, {
        headers: getAuthHeaders()
      });
      
      if (automatRes.ok) {
        const automat = await automatRes.json();
        console.log(`[ScanResolver] Automat gefunden: ${automat.code} (Status: ${automat.status})`);
        
        result.type = ScanResultType.AUTOMAT;
        result.data = automat;
        result.message = `Automat gefunden: ${automat.code}`;
        
        // Berechtigungs-Check für Helper
        if (user?.role === 'helper') {
          const allowedStatuses = ['Lager VOLL', 'Lager LEER', 'defekt'];
          if (!allowedStatuses.includes(automat.status)) {
            result.type = ScanResultType.ERROR;
            result.message = 'Automat ist nicht im Lager - kein Zugriff für Helper';
            result.navigateTo = null;
            return result;
          }
        }
        
        // Navigations-Ziel basierend auf Kontext und Status
        switch (context) {
          case ScanContext.BEFUELLUNG:
            result.navigateTo = `/befuellung/${automat.id}`;
            break;
            
          case ScanContext.AUSZAEHLEN:
            result.navigateTo = `/automaten/auszaehlen/${automat.qr_value || normalizedCode}`;
            break;
            
          case ScanContext.STANDORT_ZUWEISUNG:
            // Rückgabe des Automaten für Zuweisung, keine Navigation
            result.navigateTo = null;
            break;
            
          case ScanContext.AUTOMAT_SUCHE:
          case ScanContext.UNIVERSAL:
          default:
            // Intelligente Weiterleitung basierend auf Status
            if (automat.status === 'AUSZÄHLEN' && user?.role === 'admin') {
              result.navigateTo = `/automaten/auszaehlen/${automat.qr_value || normalizedCode}`;
            } else if (['Lager VOLL', 'Lager LEER', 'defekt'].includes(automat.status)) {
              result.navigateTo = `/befuellung/${automat.id}`;
            } else {
              result.navigateTo = `/automaten/${automat.id}`;
            }
            break;
        }
        
        return result;
      }
    } catch (e) {
      console.log(`[ScanResolver] Kein Automat gefunden`);
    }

    // 3. PRODUKT SUCHEN (falls unterstützt)
    try {
      console.log(`[ScanResolver] Suche Produkt...`);
      const produktRes = await fetch(`${apiUrl}/produkte/qr/${encodeURIComponent(normalizedCode)}`, {
        headers: getAuthHeaders()
      });
      
      if (produktRes.ok) {
        const produkt = await produktRes.json();
        console.log(`[ScanResolver] Produkt gefunden: ${produkt.name}`);
        
        result.type = ScanResultType.PRODUKT;
        result.data = produkt;
        result.message = `Produkt gefunden: ${produkt.name}`;
        result.navigateTo = `/produkte`;
        
        return result;
      }
    } catch (e) {
      // Produkte haben möglicherweise keinen QR-Endpunkt - ignorieren
    }

    // 4. NICHT GEFUNDEN
    console.log(`[ScanResolver] Code nicht gefunden: '${normalizedCode}'`);
    result.type = ScanResultType.UNKNOWN;
    result.message = `Code "${normalizedCode}" nicht gefunden`;
    result.navigateTo = null;
    
    return result;

  } catch (error) {
    console.error(`[ScanResolver] Fehler:`, error);
    return {
      type: ScanResultType.ERROR,
      code: normalizedCode,
      message: error.message || 'Fehler beim Scannen',
      data: null,
      navigateTo: null
    };
  }
};

/**
 * Hilfsfunktion: Auth-Headers für Fetch
 */
function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
}

/**
 * Validierungen für spezifische Kontexte
 */
export const validateForContext = (result, context, options = {}) => {
  if (result.type === ScanResultType.AUTOMAT) {
    const automat = result.data;
    
    switch (context) {
      case ScanContext.STANDORT_ZUWEISUNG:
        // Nur "Lager VOLL" Automaten dürfen zugewiesen werden
        if (automat.status !== 'Lager VOLL') {
          return {
            valid: false,
            error: `Automat ${automat.code} hat Status "${automat.status}". Nur Automaten mit Status "Lager VOLL" können zugewiesen werden.`
          };
        }
        // Prüfen ob bereits an einem Standort
        if (automat.current_location_id && !options.allowReassign) {
          return {
            valid: false,
            error: `Automat ${automat.code} ist bereits an einem Standort zugewiesen.`
          };
        }
        // Prüfen ob defekt
        if (automat.is_defect && !options.allowDefect) {
          return {
            valid: false,
            error: `Automat ${automat.code} ist als defekt markiert.`
          };
        }
        break;
        
      case ScanContext.BEFUELLUNG:
        // Automaten im Lager können befüllt werden
        if (!['Lager VOLL', 'Lager LEER', 'defekt'].includes(automat.status)) {
          return {
            valid: false,
            error: `Automat ${automat.code} ist nicht im Lager und kann nicht befüllt werden.`
          };
        }
        break;
        
      case ScanContext.AUSZAEHLEN:
        // Nur Automaten mit Status AUSZÄHLEN
        if (automat.status !== 'AUSZÄHLEN') {
          return {
            valid: false,
            error: `Automat ${automat.code} hat Status "${automat.status}" und muss nicht ausgezählt werden.`
          };
        }
        break;
    }
  }
  
  return { valid: true };
};

/**
 * Convenience Hook für React-Komponenten
 */
export const useScanResolver = () => {
  const resolve = async (qrValue, api, user, context) => {
    return await resolveScan({ qrValue, api, user, context });
  };
  
  return { resolve, ScanResultType, ScanContext, validateForContext };
};

export default resolveScan;
